import { HttpParams } from '@angular/common/http';

import { toHTTPParam } from '../../../core';
import { Filter } from '../../../models';
import {
  isPeriodicidadePeriodo,
  Periodicidade,
  periodicidadeMensal
} from '../../../shared';
import { Cidade } from '../../cidade';
import { Pessoa } from '../../pessoa';
import {
  PedidoReferenciaData,
  referenciaDataEmissao
} from '../referencia-data';
import { getStatusesConsultaPedidos } from '../status';

export class PedidoFilter implements Filter {
  isFilterDirty = false;

  // Periodicidade Vigência
  referenciaData: number;
  periodicidadeVigencia: number;
  dataInicioVigencia: Date;
  dataTerminoVigencia: Date;
  dataInicioVigenciaPeriodo: Date;
  dataTerminoVigenciaPeriodo: Date;
  mesVigencia: number;
  trimestreVigencia: number;
  semestreVigencia: number;
  anoVigencia: number;

  numero: number;
  numeroErp: string;
  numeroEcommerce: string;
  origensLancamentos: number[] = [];
  idsCanaisVenda: number[] = [];
  numeroCanalVenda: string;
  tiposVendas: number[] = [];
  status: number[] = [];
  statusAmostras: number[] = [];
  operacoesVenda: number[] = [];
  operacoesContaOrdem: number[] = [];
  setoresGep: number[] = [];
  idPessoa: number;
  pessoa: Pessoa;
  idsClassificacoesClientes: number[] = [];
  classificacaoClienteNull = false;
  idsClassificacoesItens: number[] = [];
  classificacaoItemNull = false;
  idsUnidadesNegocios: number[] = [];
  idsLinhasProducoes: number[] = [];
  idsItensMarcas: number[] = [];
  idsItensLinhas: number[] = [];

  // Classificação de Faturamento Pretendida
  idsClassificacoesFaturamentosPretendidas: number[] = [];
  classificacaoFaturamentoPretendidaNull: boolean;
  indicadoresCustomersSharesPretendidos: number[] = [];

  idsEstadosClientes: number[] = [];
  idCidadeCliente: number;
  cidadeCliente: Cidade;
  idsCidadesSubRegioesClientes: number[] = [];
  idsCidadesSubRegioesBairrosClientes: number[] = [];
  idsLocaisFaturamentos: number[] = [];
  idsTabelasPrecos: number[] = [];
  idsRepresentantes: number[] = [];
  somenteRepresentantesAtivos = true;
  considerarSomentePedidosRepresentante: boolean;
  idsClientesTipos: number[] = [];
  idsCampanhas: number[] = [];
  campanhaNull = false;
  somenteCampanhasEmAndamento = true;
  idsAreasCampanhas: number[] = [];
  idsPacotesCampanhas: number[] = [];
  somentePacotesCampanhasAtivos = true;
  idPessoaCampanha: number;
  pessoaCampanha: Pessoa;
  idsItens: number[] = [];
  tiposPagamentos: number[] = [];
  idsFormasPagamentos: number[] = [];
  idsCondicoesPagamentos: number[] = [];
  cuponsDesconto: string[] = [];
  statusesClicheria: number[] = [];
  idsTagsClientes: number[] = []; // IDs das tags associadas aos clientes
  idsTagsItens: number[] = []; // IDs das tags associadas aos itens
  idsGruposRentabilidade: number[] = []; // IDs dos grupos de rentabilidade
  idsGerentes: number[] = [];
  idsAtendentes: number[] = [];

  constructor(
    referenciaData: PedidoReferenciaData = referenciaDataEmissao,
    periodicidadeVigencia: Periodicidade = periodicidadeMensal
  ) {
    this.referenciaData = referenciaData
      ? referenciaData.id
      : referenciaDataEmissao.id;
    this.periodicidadeVigencia = periodicidadeVigencia
      ? periodicidadeVigencia.id
      : null;
    this.setDefaults();
  }

  /** Seta os filtros padrões */
  private setDefaults() {
    this.setDefaultStatusesPedidos();
  }

  /** Seta os status dos pedidos padrões para filtros os pedidos */
  private setDefaultStatusesPedidos() {
    this.status = [];
    const statuses = getStatusesConsultaPedidos(true, true, false);
    statuses?.forEach((s) => this.status.push(s.id));
  }

  createParams(params: HttpParams): HttpParams {
    if (!params) {
      params = new HttpParams();
    }

    // Referência da data
    if (this.referenciaData) {
      params = params.append('referenciaData', this.referenciaData.toString());
    }

    // Periodicidade Vigência
    if (this.periodicidadeVigencia) {
      params = params.append(
        'periodicidadeVigencia',
        this.periodicidadeVigencia.toString()
      );
    }

    if (isPeriodicidadePeriodo(this.periodicidadeVigencia)) {
      // Data Início Vigência
      if (this.dataInicioVigenciaPeriodo) {
        params = params.append(
          'dataInicioVigencia',
          toHTTPParam(this.dataInicioVigenciaPeriodo)
        );
      }
      // Data Término Vigência
      if (this.dataTerminoVigenciaPeriodo) {
        params = params.append(
          'dataTerminoVigencia',
          toHTTPParam(this.dataTerminoVigenciaPeriodo)
        );
      }
    } else {
      // Data Início Vigência
      if (this.dataInicioVigencia) {
        params = params.append(
          'dataInicioVigencia',
          toHTTPParam(this.dataInicioVigencia)
        );
      }
      // Data Término Vigência
      if (this.dataTerminoVigencia) {
        params = params.append(
          'dataTerminoVigencia',
          toHTTPParam(this.dataTerminoVigencia)
        );
      }
    }

    // Número
    if (this.numero) {
      params = params.append('numero', this.numero.toString());
    }
    // Número ERP
    if (this.numeroErp) {
      params = params.append('numeroErp', this.numeroErp);
    }

    // Número E-commerce
    if (this.numeroEcommerce) {
      params = params.append('numeroEcommerce', this.numeroEcommerce);
    }

    // Origens Lançamentos
    this.origensLancamentos?.forEach((origemLancamento) => {
      params = params.append('origemLancamento', origemLancamento.toString());
    });

    // Canais de Venda
    this.idsCanaisVenda?.forEach((idCanalVenda) => {
      params = params.append('idCanalVenda', idCanalVenda.toString());
    });

    // Número Canal de Venda
    if (this.numeroCanalVenda) {
      params = params.append('numeroCanalVenda', this.numeroCanalVenda);
    }

    // Tipos de Vendas
    this.tiposVendas?.forEach((tipoVenda) => {
      params = params.append(
        'tipoVenda',
        tipoVenda ? tipoVenda.toString() : null
      );
    });

    // Status
    this.status?.forEach((s) => {
      params = params.append('status', s ? s.toString() : null);
    });
    // Status Amostras
    this.statusAmostras?.forEach((statusAmostra) => {
      params = params.append(
        'statusAmostra',
        statusAmostra ? statusAmostra.toString() : null
      );
    });

    // Operacoes Venda
    this.operacoesVenda?.forEach((operacaoVenda) => {
      if (operacaoVenda) {
        params = params.append('operacaoVenda', operacaoVenda.toString());
      }
    });

    // Operacoes Conta e Ordem
    this.operacoesContaOrdem?.forEach((operacaoContaOrdem) => {
      if (operacaoContaOrdem) {
        params = params.append(
          'operacaoContaOrdem',
          operacaoContaOrdem.toString()
        );
      }
    });

    // Setores GEP
    this.setoresGep?.forEach((setorGep) => {
      params = params.append('setorGep', setorGep ? setorGep.toString() : null);
    });

    // Pessoa
    if (this.idPessoa) {
      params = params.append('idPessoa', this.idPessoa.toString());
    }

    // Classificações de clientes
    this.idsClassificacoesClientes?.forEach((idClassificacaoCliente) => {
      if (idClassificacaoCliente > 0) {
        params = params.append(
          'idClassificacaoCliente',
          idClassificacaoCliente.toString()
        );
      }
    });
    // Sem classificação de cliente
    if (this.classificacaoClienteNull) {
      params = params.append('classificacaoClienteNull', 'true');
    }

    // Classificações de itens
    this.idsClassificacoesItens?.forEach((idClassificacaoItem) => {
      if (idClassificacaoItem > 0) {
        params = params.append(
          'idClassificacaoItem',
          idClassificacaoItem.toString()
        );
      }
    });
    // Sem classificação de item
    if (this.classificacaoItemNull) {
      params = params.append('classificacaoItemNull', 'true');
    }

    // Unidades de negócio
    this.idsUnidadesNegocios?.forEach((idUnidadeNegocio) => {
      params = params.append('idUnidadeNegocio', idUnidadeNegocio.toString());
    });
    // Linhas de produção
    this.idsLinhasProducoes?.forEach((idLinhaProducao) => {
      params = params.append('idLinhaProducao', idLinhaProducao.toString());
    });
    // Marcas
    this.idsItensMarcas?.forEach((idItemMarca) => {
      params = params.append('idItemMarca', idItemMarca.toString());
    });
    // Linhas
    this.idsItensLinhas?.forEach((idItemLinha) => {
      params = params.append('idItemLinha', idItemLinha.toString());
    });

    // Classificação de faturamento pretendida
    if (
      this.idsClassificacoesFaturamentosPretendidas &&
      this.idsClassificacoesFaturamentosPretendidas.length > 0
    ) {
      this.idsClassificacoesFaturamentosPretendidas.forEach(
        (idClassificacaoFaturamentoPretendida) => {
          if (idClassificacaoFaturamentoPretendida > 0) {
            params = params.append(
              'idClassificacaoFaturamentoPretendida',
              idClassificacaoFaturamentoPretendida.toString()
            );
          }
        }
      );
    }
    // Sem classificação de faturamento pretendida
    if (
      this.classificacaoFaturamentoPretendidaNull != null &&
      this.classificacaoFaturamentoPretendidaNull !== undefined
    ) {
      params = params.append(
        'classificacaoFaturamentoPretendidaNull',
        this.classificacaoFaturamentoPretendidaNull.toString()
      );
    }

    // Indicadores Customer Share Pretendidos
    this.indicadoresCustomersSharesPretendidos?.forEach(
      (indicadorCustomerSharePretendido) => {
        if (indicadorCustomerSharePretendido > 0) {
          params = params.append(
            'indicadorCustomerSharePretendido',
            indicadorCustomerSharePretendido.toString()
          );
        }
      }
    );

    // Estados dos clientes
    if (this.idsEstadosClientes?.length > 0) {
      this.idsEstadosClientes.forEach((idEstadoCliente) => {
        params = params.append('idEstadoCliente', idEstadoCliente.toString());
      });
    }
    // Cidades dos clientes
    if (this.idCidadeCliente) {
      params = params.append(
        'idCidadeCliente',
        this.idCidadeCliente.toString()
      );
    }
    // Subregiões das cidades dos clientes
    if (this.idsCidadesSubRegioesClientes?.length > 0) {
      this.idsCidadesSubRegioesClientes.forEach((idCidadeSubRegiaoCliente) => {
        params = params.append(
          'idCidadeSubRegiaoCliente',
          idCidadeSubRegiaoCliente.toString()
        );
      });
    }
    // Bairros das subregiões das cidades dos clientes
    if (this.idsCidadesSubRegioesBairrosClientes?.length > 0) {
      this.idsCidadesSubRegioesBairrosClientes.forEach(
        (idCidadeSubRegiaoBairroCliente) => {
          params = params.append(
            'idCidadeSubRegiaoBairroCliente',
            idCidadeSubRegiaoBairroCliente.toString()
          );
        }
      );
    }

    // Representantes
    this.idsRepresentantes?.forEach((idRepresentante) => {
      params = params.append('idRepresentante', idRepresentante.toString());
    });
    // Se deve considerar somente pedidos dos representantes selecionados
    if (this.idsRepresentantes?.length > 0) {
      if (
        this.considerarSomentePedidosRepresentante != null &&
        this.considerarSomentePedidosRepresentante !== undefined
      ) {
        params = params.append(
          'considerarSomentePedidosRepresentante',
          this.considerarSomentePedidosRepresentante.toString()
        );
      }
    }

    // Locais de Faturamentos
    this.idsLocaisFaturamentos?.forEach((idLocalFaturamento) => {
      params = params.append(
        'idLocalFaturamento',
        idLocalFaturamento.toString()
      );
    });

    // Tabelas de Preços
    this.idsTabelasPrecos?.forEach((idTabelaPreco) => {
      params = params.append('idTabelaPreco', idTabelaPreco.toString());
    });

    // Tipos de Clientes
    this.idsClientesTipos?.forEach((idClienteTipo) => {
      params = params.append('idClienteTipo', idClienteTipo.toString());
    });

    // IDs Campanhas
    this.idsCampanhas?.forEach((idCampanha) => {
      if (idCampanha > 0) {
        params = params.append('idCampanha', idCampanha.toString());
      }
    });
    // Sem campanha
    if (this.campanhaNull) {
      params = params.append('campanhaNull', 'true');
    }

    // Áreas das campanhas
    this.idsAreasCampanhas?.forEach((idAreaCampanha) => {
      params = params.append('idAreaCampanha', idAreaCampanha.toString());
    });

    // Pacotes das campanhas
    this.idsPacotesCampanhas?.forEach((idPacoteCampanha) => {
      params = params.append('idPacoteCampanha', idPacoteCampanha.toString());
    });

    // Pessoa da campanha
    if (this.idPessoaCampanha) {
      params = params.append(
        'idPessoaCampanha',
        this.idPessoaCampanha.toString()
      );
    }

    // Itens
    this.idsItens?.forEach((idItem) => {
      params = params.append('idItem', idItem.toString());
    });

    // Tipos de pagamentos
    this.tiposPagamentos?.forEach((tipoPagamento) => {
      params = params.append('tipoPagamento', tipoPagamento.toString());
    });

    // Formas de pagamentos
    this.idsFormasPagamentos?.forEach((idFormaPagamento) => {
      params = params.append('idFormaPagamento', idFormaPagamento.toString());
    });

    // Condições de pagamentos
    this.idsCondicoesPagamentos?.forEach((idCondicaoPagamento) => {
      params = params.append(
        'idCondicaoPagamento',
        idCondicaoPagamento.toString()
      );
    });

    // Cupons de desconto
    this.cuponsDesconto?.forEach((cupomDesconto) => {
      if (cupomDesconto) {
        params = params.append('cupomDesconto', cupomDesconto);
      }
    });

    // Statuses Clicheria
    this.statusesClicheria?.forEach((statusClicheria) => {
      if (statusClicheria) {
        params = params.append('statusClicheria', statusClicheria.toString());
      }
    });

    // Tags de clientes
    if (this.idsTagsClientes && this.idsTagsClientes.length > 0) {
      this.idsTagsClientes.forEach((idTag) => {
        params = params.append('idsTagsClientes', idTag.toString());
      });
    }

    // Tags de Itens
    if (this.idsTagsItens && this.idsTagsItens.length > 0) {
      this.idsTagsItens.forEach((idTag) => {
        params = params.append('idsTagsItens', idTag.toString());
      });
    }

    // Grupos de rentabilidade
    if (this.idsGruposRentabilidade && this.idsGruposRentabilidade.length > 0) {
      this.idsGruposRentabilidade.forEach((idGrupo) => {
        params = params.append('idsGruposRentabilidade', idGrupo.toString());
      });
    }

    // Gerentes
    if (this.idsGerentes && this.idsGerentes.length > 0) {
      this.idsGerentes.forEach((idGerente) => {
        params = params.append('idsGerentes', idGerente.toString());
      });
    }

    // Atendentes
    if (this.idsAtendentes && this.idsAtendentes.length > 0) {
      this.idsAtendentes.forEach((idAtendente) => {
        params = params.append('idsAtendentes', idAtendente.toString());
      });
    }

    return params;
  }

  clear() {
    this.isFilterDirty = false;

    this.numero = null;
    this.numeroErp = null;
    this.numeroEcommerce = null;
    this.origensLancamentos = [];
    this.idsCanaisVenda = [];
    this.numeroCanalVenda = null;
    this.tiposVendas = [];
    this.status = [];
    this.statusAmostras = [];
    this.operacoesVenda = [];
    this.operacoesContaOrdem = [];
    this.setoresGep = [];
    this.idPessoa = null;
    this.pessoa = null;
    this.idsClassificacoesClientes = [];
    this.classificacaoClienteNull = false;
    this.idsClassificacoesItens = [];
    this.classificacaoItemNull = false;
    this.idsUnidadesNegocios = [];
    this.idsLinhasProducoes = [];
    this.idsItensMarcas = [];
    this.idsItensLinhas = [];

    this.idsClassificacoesFaturamentosPretendidas = [];
    this.classificacaoFaturamentoPretendidaNull = false;
    this.indicadoresCustomersSharesPretendidos = [];

    this.idsEstadosClientes = [];
    this.idCidadeCliente = null;
    this.cidadeCliente = null;
    this.idsCidadesSubRegioesClientes = [];
    this.idsCidadesSubRegioesBairrosClientes = [];

    this.idsLocaisFaturamentos = [];
    this.idsTabelasPrecos = [];
    this.idsRepresentantes = [];
    this.somenteRepresentantesAtivos = true;
    this.considerarSomentePedidosRepresentante = null;
    this.idsClientesTipos = [];
    this.idsCampanhas = [];
    this.campanhaNull = false;
    this.somenteCampanhasEmAndamento = true;
    this.idsAreasCampanhas = [];
    this.idsPacotesCampanhas = [];
    this.somentePacotesCampanhasAtivos = true;
    this.idPessoaCampanha = null;
    this.pessoaCampanha = null;
    this.idsItens = [];
    this.tiposPagamentos = [];
    this.idsFormasPagamentos = [];
    this.idsCondicoesPagamentos = [];
    this.cuponsDesconto = [];
    this.statusesClicheria = [];
    this.idsTagsClientes = [];
    this.idsTagsItens = [];
    this.idsGruposRentabilidade = [];
    this.idsGerentes = [];
    this.idsAtendentes = [];
    this.setDefaults();
  }

  /** Verificar se o filtro possui a periodicidade da vigência considerando a periodicidade e data de início da vigência */
  possuiPeriodicidadeVigencia(): boolean {
    // Periodicidade Vigência
    if (!this.periodicidadeVigencia) {
      return false;
    }

    // Para periodicidade "período", verificar as datas de início e término do período
    if (isPeriodicidadePeriodo(this.periodicidadeVigencia)) {
      return !!(
        this.dataInicioVigenciaPeriodo && this.dataTerminoVigenciaPeriodo
      );
    }

    // Data Início Vigência
    if (!this.dataInicioVigencia) {
      return false;
    }

    return true;
  }
}
